/**
 * Shell component.
 * 
 * Provides the layout of the host application, including navigation and dynamic routing.
 * It uses the userContext from AuthContext to build the navigation pane and renders the
 * DynamicRoutes component for MFE content.
 *
 * @module Shell
 */

import React, { useEffect, useState, useContext } from 'react';
import { Box } from '@mui/material';;
import Loader from '../components/Loader';
import { useNavigate, useLocation, Route, Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import DynamicRoutes from './DynamicRoutes';
import './style.css'
import NavDrawer from './sidenav/NavDrawer';
import appInsights from '../utils/telemetry';
import Breadcrumb from '../components/Breadcrumb';
import { RemoteComponentStoreProvider } from '../context/RemoteComponentStoreContext';
import { handleNavigationEvent } from '../utils/utils';
import LastRefreshedTimeInfo from '../components/LastRefreshedTimeInfo';

const Shell: React.FC<{ children?: React.ReactNode }> = () => {
    const { userContext } = useContext(AuthContext) || {};
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        // Handle navigation events from remote modules
        const cleanup = handleNavigationEvent(navigate);
        return cleanup; // Cleanup the event listener on unmount
    }, []);

    useEffect(() => {
        // Once userContext is set (or confirmed missing), stop loading.
        setLoading(false);
        appInsights?.setAuthenticatedUserContext(
            userContext?.userProfile?.loginId || '',
            userContext?.userProfile?.email || undefined
        );
    }, [userContext, navigate]);

    useEffect(() => {
        // Track page view on route change
        appInsights?.trackPageView();
    }, [location]);

    useEffect(() => {
        // Track tab changes fired by remote MFEs via CustomEvent
        const handleTabChange = (event: Event) => {
            const detail = (event as CustomEvent)?.detail;
            if (!detail || typeof detail.tabName !== 'string' || !detail.tabName) return;
            const { tabName, tabIndex, page } = detail;
            appInsights?.trackEvent({
                name: 'TabView',
                properties: {
                    tabName,
                    tabIndex: Number.isFinite(tabIndex) ? tabIndex : undefined,
                    page: typeof page === 'string' ? page : window.location.pathname,
                    userId: userContext?.userProfile?.loginId || '',
                }
            });
        };
        window.addEventListener('vision:tab-change', handleTabChange);
        return () => window.removeEventListener('vision:tab-change', handleTabChange);
    }, [userContext]);


    if (loading) {
        return <Loader />;
    }

    if (!userContext) {
        return null;
    }

    const { userProfile, navConfig } = userContext;
    const navItems = navConfig?.items || []
    const bottomGroup = navConfig.bottomGroupList

    return (
        <RemoteComponentStoreProvider>
            <Box className="vision-outer-shell">
                <Box className="nav-container">
                    <NavDrawer userProfile={userProfile} navItems={navItems} bottomGroup={bottomGroup} />
                </Box>
                <Box className="main-app-container">
                    <Breadcrumb navItems={navItems}>
                        <LastRefreshedTimeInfo />
                    </Breadcrumb>
                    <DynamicRoutes navItems={navItems} />
                </Box>
            </Box>
        </RemoteComponentStoreProvider>
    );
};

export default Shell;